import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import useLeadSubmit, { Honeypot, LeadError } from '../useLeadSubmit';
import { sectionStyles, getAnimation, hexToRgba } from '../utils';
import SectionDivider from './SectionDivider';

export default function NewsletterSection({ block = {}, brand, primaryColor, secondaryColor, enableAnimations, currentPage }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'centered';

    const [email, setEmail] = useState('');
    const leadSubmit = useLeadSubmit({
        brandId: brand?.id,
        blockType: 'newsletter',
        blockId: block.id,
        pageId: currentPage?.id || null,
        source: 'Newsletter',
    });
    const submitted = leadSubmit.submitted;

    const title = content.title || 'Suscribete a nuestro boletin';
    const subtitle = content.subtitle || 'Recibe noticias, eventos y novedades directamente en tu correo';
    const placeholder = content.placeholder || 'Tu correo electronico';
    const ctaText = content.cta_text || 'Suscribirme';
    const successMessage = content.success_message || 'Gracias por suscribirte. Pronto recibiras noticias.';

    const handleSubmit = (e) => {
        e.preventDefault();
        if (email.includes('@')) leadSubmit.submit({ email });
    };

    // ── Banner variant (horizontal inline) ──
    if (variant === 'banner') {
        return (
            <section className={`py-10 md:py-14 relative ${s.radiusClass} ${s.shadowClass}`} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="text-center md:text-left">
                            <h2 className="text-2xl md:text-3xl font-bold" style={{ color: s.text }}>{title}</h2>
                            {subtitle && <p className="mt-2 text-sm" style={{ color: s.textSub }}>{subtitle}</p>}
                        </div>
                        {submitted ? (
                            <div className="flex items-center gap-2 px-6 py-3 rounded-xl border"
                                style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.08)' : hexToRgba(s.accent, 0.08), borderColor: s.dark ? 'rgba(255,255,255,0.15)' : hexToRgba(s.accent, 0.25) }}>
                                <svg className="w-5 h-5" style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
                                <span className="text-sm font-medium" style={{ color: s.text }}>{successMessage}</span>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} style={{position:'relative'}} className="flex gap-2 w-full md:w-auto">
                                <Honeypot onFill={leadSubmit.setHoneypot} />
                                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={placeholder}
                                    className="flex-1 md:w-64 px-4 py-3 rounded-xl border text-sm focus:ring-2 focus:outline-none"
                                    style={{ '--tw-ring-color': s.accent, borderColor: s.dark ? 'rgba(255,255,255,0.2)' : '#e5e7eb', backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#ffffff', color: s.text }} required />
                                <button type="submit" className="px-6 py-3 rounded-xl text-white text-sm font-semibold whitespace-nowrap hover:opacity-90 transition-opacity" style={{ backgroundColor: s.accent }}>
                                    {ctaText}
                                </button>
                            </form>
                        )}
                        <LeadError error={leadSubmit.error} />
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent" position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Split variant (text left, form right) ──
    if (variant === 'split') {
        return (
            <section className={`${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`} style={s.bgStyle}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-3xl md:text-4xl font-bold" style={{ color: s.text }}>{title}</h2>
                            {subtitle && <p className="mt-4 text-lg" style={{ color: s.textSub }}>{subtitle}</p>}
                        </div>
                        <div className="p-8 rounded-2xl" style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)' }}>
                            {submitted ? (
                                <div className="text-center py-6">
                                    <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: s.accent }}>
                                        <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
                                    </div>
                                    <p className="text-lg font-semibold" style={{ color: s.text }}>{successMessage}</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} style={{position:'relative'}} className="space-y-4">
                                    <Honeypot onFill={leadSubmit.setHoneypot} />
                                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={placeholder}
                                        className="w-full px-4 py-3.5 rounded-xl border text-sm focus:ring-2 focus:outline-none"
                                        style={{ '--tw-ring-color': s.accent, borderColor: s.dark ? 'rgba(255,255,255,0.2)' : '#e5e7eb', backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#ffffff', color: s.text }} required />
                                    <button type="submit" className="w-full px-6 py-3.5 rounded-xl text-white text-sm font-semibold hover:opacity-90 transition-opacity" style={{ backgroundColor: s.accent }}>
                                        {ctaText}
                                    </button>
                                    <p className="text-xs text-center" style={{ color: s.textSub }}>{content.privacy_text || 'Respetamos tu privacidad. Sin spam.'}</p>
                                </form>
                            )}
                            <LeadError error={leadSubmit.error} />
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color="transparent" position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Centered variant (default) ──
    return (
        <section className={`${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`} style={s.bgStyle}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <div className="max-w-xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold" style={{ color: s.text }}>{title}</h2>
                    {subtitle && <p className="mt-4" style={{ color: s.textSub }}>{subtitle}</p>}
                    <div className="mt-8">
                        {submitted ? (
                            <div className="flex items-center justify-center gap-3 py-4">
                                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: s.accent }}>
                                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
                                </div>
                                <p className="text-sm font-medium" style={{ color: s.text }}>{successMessage}</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} style={{position:'relative'}} className="flex flex-col sm:flex-row gap-3">
                                <Honeypot onFill={leadSubmit.setHoneypot} />
                                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={placeholder}
                                    className="flex-1 px-5 py-3.5 rounded-xl border text-sm focus:ring-2 focus:outline-none"
                                    style={{ '--tw-ring-color': s.accent, borderColor: s.dark ? 'rgba(255,255,255,0.2)' : '#e5e7eb', backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#ffffff', color: s.text }} required />
                                <button type="submit" className="px-8 py-3.5 rounded-xl text-white text-sm font-semibold whitespace-nowrap hover:opacity-90 transition-opacity shadow-lg" style={{ backgroundColor: s.accent }}>
                                    {ctaText}
                                </button>
                            </form>
                        )}
                        <LeadError error={leadSubmit.error} />
                    </div>
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color="transparent" position="bottom"  config={config}/>
        </section>
    );
}
